import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Briefcase, MapPin, Building2, DollarSign, BookmarkPlus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const COUNTRIES = ["UAE", "Saudi Arabia", "Qatar", "Oman", "Kuwait", "Bahrain", "India", "UK"];
const STATUSES = ["saved", "applied", "shortlisted", "interview", "selected", "rejected"];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [saved, setSaved] = useState([]);
  const [country, setCountry] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("browse");

  const load = () => {
    const params = {};
    if (country) params.location = country;
    if (q) params.q = q;
    api.get("/jobs", { params }).then(r => setJobs(r.data.jobs));
    api.get("/jobs/saved/me").then(r => setSaved(r.data.saved)).catch(()=>{});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country, q]);

  const saveJob = async (job, status = "saved") => {
    await api.post("/jobs/save", { job_id: job.job_id, status });
    toast.success(`Job ${status === "saved" ? "saved" : "updated"}`);
    load();
  };

  const updateStatus = async (job_id, status) => {
    const item = saved.find(s => s.job_id === job_id);
    await api.post("/jobs/save", { job_id, status, notes: item?.notes || "" });
    load();
  };

  return (
    <div className="space-y-5" data-testid="jobs-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">HSE Jobs</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Search, save and track your applications.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {["browse", "tracker"].map(t => (
          <button key={t} data-testid={`jobs-tab-${t}`} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 ${tab === t ? "border-amber-500 text-amber-600" : "border-transparent text-slate-500"}`}>{t === "browse" ? "Browse jobs" : `Applications (${saved.length})`}</button>
        ))}
      </div>

      {tab === "browse" && (
        <>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="jobs-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by title or company…" className="pl-9 h-11" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button data-testid="filter-country-all" onClick={()=>setCountry("")} className={`pill ${country === "" ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>All</button>
              {COUNTRIES.map(c => (
                <button key={c} data-testid={`filter-country-${c}`} onClick={()=>setCountry(c)}
                        className={`pill whitespace-nowrap ${country === c ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {jobs.map(j => (
              <div key={j.job_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 card-hover">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold truncate">{j.title}</h3>
                    <div className="text-sm text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> {j.company}</div>
                  </div>
                  <button data-testid={`save-job-${j.job_id}`} onClick={()=>saveJob(j)} className="btn-outline !py-1.5 !px-3 text-xs"><BookmarkPlus className="h-3 w-3" /> Save</button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location || j.country}</span>
                  <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" />{j.salary || "—"}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" />{j.experience || "—"}</span>
                </div>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{j.description}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "tracker" && (
        <div className="space-y-3">
          {saved.length === 0 && <div className="text-sm text-slate-500">No saved jobs yet.</div>}
          {saved.map(s => (
            <div key={s.job_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-semibold">{s.job_snapshot.title}</div>
                  <div className="text-xs text-slate-500">{s.job_snapshot.company} · {s.job_snapshot.location || s.job_snapshot.country}</div>
                </div>
                <select data-testid={`status-${s.job_id}`} value={s.status} onChange={(e)=>updateStatus(s.job_id, e.target.value)}
                        className="rounded-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-1 text-xs font-semibold capitalize">
                  {STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
